import { css, cx } from '@emotion/css';
import { BaseStyle, Color, Font, FontWeight, SpaceSize, Spacing, TextAlign } from 'src/styles';

import { MButtonSize } from './MButton';

const getButtonSize = ({ size }: { size: MButtonSize }) => {
  switch (size) {
    case 'small': {
      return css`
        height: ${SpaceSize.is32} !important;
      `;
    }

    case 'middle': {
      return css`
        height: ${SpaceSize.is48} !important;
      `;
    }

    case 'large': {
      return css`
        height: ${SpaceSize.is60} !important;
      `;
    }

    default: {
      return css`
        height: ${SpaceSize.is48} !important;
      `;
    }
  }
};

const getButtonFont = ({ size }: { size: MButtonSize }) => {
  switch (size) {
    case 'small': {
      return Font.sm;
    }

    case 'middle': {
      return Font.md;
    }

    case 'large': {
      return Font.lg;
    }

    default: {
      return Font.md;
    }
  }
};

const baseStyle = ({ size }: { size: MButtonSize }) => css`
  ${TextAlign.center}
  ${BaseStyle.level}
  ${Spacing.p8}
  width: fit-content !important;
  min-width: 9.375rem;
  border-radius: 4rem !important;
  ${FontWeight.bold}
  ${getButtonSize({ size })}
  ${getButtonFont({ size })}

  /* antd's a.ant-btn removes top padding on link buttons, which pushes the label up. */
  && {
    padding-top: ${SpaceSize.is8} !important;
  }
`;

export const primary = ({ size, danger }: { size: MButtonSize; danger: boolean }) =>
  cx(
    baseStyle({ size }),
    css`
      background-color: ${danger ? Color.Red6 : Color.Brand6} !important;
      color: ${Color.Gray1} !important;
      border-color: ${danger ? Color.Red6 : Color.Brand6} !important;

      &:focus {
        background-color: ${danger ? Color.Red8 : Color.Brand7} !important;
        color: ${Color.Gray1} !important;
        border-color: ${Color.Brand7} !important;
      }

      &:hover:not(:focus) {
        background-color: ${danger ? Color.Red8 : Color.Brand7} !important;
        color: ${Color.Gray1} !important;
        border-color: ${Color.Brand7} !important;
      }

      &:disabled {
        background-color: ${Color.Gray6} !important;
        color: ${Color.Gray1} !important;
        border-color: ${Color.Gray6} !important;
        pointer-events: none;
      }
    `,
  );

export const secondary = ({ size, danger }: { size: MButtonSize; danger: boolean }) =>
  cx(
    baseStyle({ size }),
    css`
      background-color: ${Color.Gray1} !important;
      color: ${danger ? Color.Red6 : Color.Brand6} !important;
      border-color: ${danger ? Color.Red6 : Color.Brand6} !important;

      &:focus {
        background-color: ${danger ? Color.Red2 : Color.Brand1} !important;
        color: ${danger ? Color.Red6 : Color.Brand6} !important;
        border-color: ${danger ? Color.Red6 : Color.Brand6} !important;
      }

      &:hover:not(:focus) {
        background-color: ${Color.Brand1} !important;
        color: ${danger ? Color.Red6 : Color.Brand6} !important;
        border-color: ${danger ? Color.Red6 : Color.Brand6} !important;
      }

      &:disabled {
        background-color: ${Color.Gray3} !important;
        color: ${Color.Gray6} !important;
        border-color: ${Color.Gray6} !important;
        pointer-events: none;
      }
    `,
  );

export const tertiary = ({ size, danger }: { size: MButtonSize; danger: boolean }) =>
  cx(
    baseStyle({ size }),
    css`
      background: transparent !important;
      color: ${danger ? Color.Red6 : Color.Brand6} !important;
      box-shadow: none;

      &:focus {
        background-color: ${danger ? Color.Red2 : Color.Brand1} !important;
        color: ${danger ? Color.Red6 : Color.Brand6} !important;
      }

      &:hover:not(:focus) {
        background-color: ${danger ? Color.Red2 : Color.Brand1} !important;
        color: ${danger ? Color.Red6 : Color.Brand6} !important;
      }

      &:disabled {
        background-color: transparent !important;
        color: ${Color.Gray6} !important;
        pointer-events: none;
      }
    `,
  );
